import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "api";
import "./Plans.css";
import PageHeader from "components/layout/page-header/PageHeader";
import AppModal from "components/ui/AppModal/AppModal";

const CACHE_DURATION = 30 * 60 * 1000;

/** Nomes ainda retornados pelo banco/cache legado → nome exibido (migração billing). */
const PLAN_DISPLAY_NAME = {
  Gratuito: "Básico",
  Núcleo: "Premium",
  Pro: "Horizonte",
  Vértice: "Horizonte",
};

function getPlanDisplayName(name) {
  if (name == null || name === "") return "";
  return PLAN_DISPLAY_NAME[name] ?? name;
}

function isHorizonteTier(plan) {
  const n = plan?.name;
  return n === "Horizonte" || n === "Pro" || n === "Vértice";
}

function formatAiFeature(aiFeature) {
  if (aiFeature == null) return "Sem tokens";
  if (aiFeature === -1)  return "Tokens ilimitados";
  if (aiFeature === 0)   return "Sem tokens";
  return `${Number(aiFeature).toLocaleString("pt-BR")} tokens/mês`;
}

function formatNumber(num) {
  if (num == null) return "—";
  return Number(num).toLocaleString("pt-BR");
}

const PLAN_FEATURES = [
  { key: "storage",        getLabel: p => `Armazenamento: ${formatNumber(p?.storage)} GB`,          isDisabled: () => false },
  { key: "projects",       getLabel: p => `Máximo de projetos: ${formatNumber(p?.max_projects)}`,   isDisabled: () => false },
  { key: "reports",        getLabel: p => `Máximo de relatórios: ${formatNumber(p?.max_reports)}`,  isDisabled: () => false },
  { key: "tokens",         getLabel: p => formatAiFeature(p?.ai_feature),                           isDisabled: () => false },
  { key: "photogrammetry", getLabel: () => "Fotogrametria (500 img ou 5 min vídeo)",                isDisabled: p => !isHorizonteTier(p) },
  { key: "period",         getLabel: () => "Durante o trial: projetos com até 30 dias; após assinar: sem limite de tempo", isDisabled: () => false },
];

/* Indicador SVG inline */
const FeatureIndicator = ({ disabled, featured }) => {
  if (disabled) {
    return (
      <span className="plan-card__feature-indicator">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <line x1="4" y1="4" x2="10" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="10" y1="4" x2="4" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return (
    <span className="plan-card__feature-indicator">
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="5" stroke={featured ? "rgba(255,255,255,0.45)" : "rgba(3,81,132,0.45)"} strokeWidth="1" />
        <circle cx="7" cy="7" r="2.2" fill={featured ? "rgba(255,255,255,0.75)" : "rgba(3,81,132,0.75)"} />
      </svg>
    </span>
  );
};

const MODAL_INITIAL = { open: false, variant: "loading", title: "", message: "", onConfirm: null, allowClose: false };

const Plans = ({ token }) => {
  const [plans, setPlans]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [userPlanName, setUserPlanName] = useState("");
  const navigate = useNavigate();

  const [modal, setModal] = useState(MODAL_INITIAL);
  const closeModal = () => setModal(MODAL_INITIAL);

  useEffect(() => {
    const fetchPlansData = async () => {
      try {
        const now     = Date.now();
        const headers = { headers: { Authorization: `Bearer ${token}` } };

        /* Planos disponíveis */
        const cachedPlans   = localStorage.getItem("cachedPlans_v2");
        const cachedPlansTs = localStorage.getItem("cachePlansTimestamp_v2");
        const plansStale    = !cachedPlans || !cachedPlansTs || now - Number(cachedPlansTs) >= CACHE_DURATION;

        let fetchedPlans;
        if (!plansStale) {
          fetchedPlans = JSON.parse(cachedPlans).sort((a, b) => a.price - b.price);
        } else {
          const res = await api.get("/plans/all", headers);
          fetchedPlans = res.data.sort((a, b) => a.price - b.price);
          localStorage.setItem("cachedPlans_v2", JSON.stringify(fetchedPlans));
          localStorage.setItem("cachePlansTimestamp_v2", now);
        }
        setPlans(fetchedPlans);

        /* Plano do usuário */
        const cachedUserPlan   = localStorage.getItem("cachedUserPlan_v2");
        const cachedUserPlanTs = localStorage.getItem("cacheUserPlanTimestamp_v2");
        const userPlanStale    = !cachedUserPlan || !cachedUserPlanTs || now - Number(cachedUserPlanTs) >= CACHE_DURATION;

        if (!userPlanStale) {
          setUserPlanName(JSON.parse(cachedUserPlan).name);
        } else {
          const res     = await api.get("/plans/user", headers);
          const userPlan = res.data[0];
          localStorage.setItem("cachedUserPlan_v2", JSON.stringify(userPlan));
          localStorage.setItem("cacheUserPlanTimestamp_v2", now);
          setUserPlanName(userPlan.name);
        }
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchPlansData();
  }, [token]);

  const handleSubscribe = async planName => {
    setModal({
      open: true,
      variant: "loading",
      title: "Processando...",
      message: "Estamos atualizando sua assinatura. Por favor, aguarde.",
      onConfirm: null,
      allowClose: false,
    });

    try {
      const headers  = { headers: { Authorization: `Bearer ${token}` } };
      const params = new URLSearchParams(window.location.search);
      const promoFromUrl = params.get("promo") || params.get("promotion_code");
      const payload = { plan_name: planName };
      if (promoFromUrl) payload.promotion_code = promoFromUrl.trim();
      const response = await api.post("/stripe/create-or-change-subscription", payload, headers);

      if (response.data.url) {
        ["cachedUserPlan", "cachedUserPlan_v2", "cachedPlanData", "cacheUserPlanTimestamp", "cacheUserPlanTimestamp_v2"].forEach(k =>
          localStorage.removeItem(k)
        );
        window.location.href = response.data.url;
        return;
      }

      if (response.data.success) {
        const now              = Date.now();
        const userPlanResponse = await api.get("/plans/user", headers);
        setModal({
          open: true,
          variant: "success",
          title: "Plano atualizado!",
          message: response.data.message || "Sua assinatura foi modificada com sucesso.",
          onConfirm: () => {
            const updatedPlan = userPlanResponse.data[0];
            localStorage.setItem("cachedUserPlan_v2",        JSON.stringify(updatedPlan));
            localStorage.setItem("cachedPlanData",           JSON.stringify(updatedPlan));
            localStorage.setItem("cacheUserPlanTimestamp_v2", now);
            window.location.reload();
          },
          allowClose: true,
        });
      } else {
        setModal({ open: true, variant: "error", title: "Erro", message: "Erro inesperado. Tente novamente.", onConfirm: closeModal, allowClose: true });
      }
    } catch (err) {
      console.error("Erro ao processar a assinatura:", err);
      setModal({
        open: true,
        variant: "error",
        title: "Erro ao processar",
        message: err.response?.data?.detail || "Erro ao processar a assinatura. Tente novamente.",
        onConfirm: closeModal,
        allowClose: true,
      });
    }
  };

  const allCards = [...plans, { name: "Empresa", custom: true }];

  return (
    <div className="plans-page">
      <PageHeader
        accent="Assinatura"
        title="Planos Vertex 4D"
        subtitle="Comece no Básico, evolua para Premium e alcance o Horizonte conforme sua operação cresce."
      />

      {loading ? (
        <div className="plans-page__loading">
          <div className="spinner-border" role="status" />
        </div>
      ) : (
        <div className="plans-grid">
          {allCards.map((plan, index) => {
            const isEnterprise = plan.custom === true;
            /* "Mais popular" só no tier Premium (nome API ou legado mapeado p/ Premium) */
            const isFeatured =
              !isEnterprise && getPlanDisplayName(plan.name) === "Premium";
            const isCurrent    =
              getPlanDisplayName(plan.name) === getPlanDisplayName(userPlanName);

            let cardClass = "plan-card";
            if (isFeatured)   cardClass += " plan-card--featured";
            if (isEnterprise) cardClass += " plan-card--enterprise";

            return (
              <div
                key={index}
                className={cardClass}
                style={{ animationDelay: `${index * 0.08}s` }}
              >
                {isFeatured && <div className="plan-card__scan" aria-hidden="true" />}

                {isFeatured && (
                  <div className="plan-card__badge">
                    <span className="plan-card__badge-dot" />
                    Mais popular
                  </div>
                )}

                {isCurrent && (
                  <div className="plan-card__current-badge">Seu plano</div>
                )}

                {/* Tier */}
                <div className="plan-card__header">
                  <div className="plan-card__tier">{getPlanDisplayName(plan.name)}</div>
                </div>

                {/* Preço */}
                <div className="plan-card__price-block">
                  {isEnterprise ? (
                    <div className="plan-card__price plan-card__price--consult">
                      Sob Consulta
                    </div>
                  ) : (
                    <>
                      <div className="plan-card__price-currency">R$</div>
                      <div className="plan-card__price">{Number(plan.price).toFixed(2)}</div>
                      <div className="plan-card__price-period">/mês</div>
                    </>
                  )}
                </div>

                <div className="plan-card__divider" />

                {/* Features */}
                <ul className="plan-card__features">
                  {isEnterprise ? (
                    <li className="plan-card__feature">
                      <FeatureIndicator disabled={false} featured={false} />
                      <span>Serviços específicos ou pontuais. Entre em contato para uma proposta personalizada.</span>
                    </li>
                  ) : (
                    PLAN_FEATURES.map(({ key, getLabel, isDisabled }) => {
                      const disabled = isDisabled(plan);
                      return (
                        <li key={key} className={`plan-card__feature${disabled ? " plan-card__feature--disabled" : ""}`}>
                          <FeatureIndicator disabled={disabled} featured={isFeatured} />
                          <span>{getLabel(plan)}</span>
                        </li>
                      );
                    })
                  )}
                </ul>

                {/* CTA */}
                {isEnterprise ? (
                  <button
                    className="plan-cta plan-cta--outline"
                    onClick={() => navigate("/home?question=Gostaria de solicitar uma cotação.")}
                  >
                    Solicitar cotação
                  </button>
                ) : isCurrent ? (
                  <button className="plan-cta plan-cta--current" disabled>
                    <span className="plan-cta__dot" />
                    Plano Atual
                  </button>
                ) : (
                  <button
                    className={`plan-cta ${isFeatured ? "plan-cta--featured" : "plan-cta--default"}`}
                    onClick={() => handleSubscribe(plan.name)}
                  >
                    Assinar agora
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" className="plan-cta__arrow" aria-hidden="true">
                      <path d="M2.5 7.5h10M9 4l3.5 3.5L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Subscription Modal ───────────────────────────────────────── */}
      <AppModal
        isOpen={modal.open}
        onClose={modal.allowClose ? closeModal : undefined}
        allowOutsideClick={modal.allowClose}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        confirmText="OK"
        onConfirm={modal.onConfirm}
        showCloseBtn={modal.allowClose}
      />
    </div>
  );
};

const mapStateToProps = state => ({ token: state.auth.accessToken });
export default connect(mapStateToProps)(Plans);
